import Breadcrumbs from "components/Common/Breadcrumb"
import React, { useEffect, useState } from "react"
import { useSelector } from "react-redux"
import { useDispatch } from "react-redux"
import { useLocation, useNavigate } from "react-router-dom"
import { getWalletCouponDetails } from "store/actions/promotionsAction"
import BackButton from "components/Shared/BackButton"
import {
  Badge,
  Card,
  CardBody,
  CardTitle,
  Col,
  Nav,
  NavItem,
  NavLink,
  Row,
  TabContent,
  TabPane,
  Table,
} from "reactstrap"
import classnames from "classnames"
import { ShimmerThumbnail } from "react-shimmer-effects"
import formatDateTime from "components/Shared/formatDateTime"
import LtrText from "components/Shared/LtrText"
import isPermissionEnabled from "common/isPermissionEnabled"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"

const CouponRedeemDetails = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation()
  document.title = t("Wallet Coupon Details") + " "

  const location = useLocation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const searchParams = new URLSearchParams(location.search)
  const id = searchParams.get("id")

  const { userRole, accountType } = useSelector(state => state.Login)

  useEffect(() => {
    const isPermissionList = isPermissionEnabled(
      userRole,
      46,
      "coupon_redeem_1",
      accountType
    )

    if (!isPermissionList) {
      navigate("/dashboard")
    }
  }, [userRole])

  const { loading, couponDetails } = useSelector(state => state.Promotions)
  const [assignedTab, setAssignedTab] = useState("assigned")

  const activeCustomers =
    couponDetails?.assignedCustomers?.filter(ac => !ac.isDeleted) || []
  const deletedCustomers =
    couponDetails?.assignedCustomers?.filter(ac => ac.isDeleted) || []
  const hasDeletedUsers = deletedCustomers.length > 0

  useEffect(() => {
    const fetchOldDetails = () => {
      const payload = {
        id,
      }
      dispatch(getWalletCouponDetails(payload))
    }
    fetchOldDetails()
  }, [])

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs />
        <Row>
          <Col lg={6}>
            <Card>
              <CardBody>
                <CardTitle className="d-flex align-items-center">
                  <BackButton /> {t("Wallet Coupon Details")}
                </CardTitle>
                <p className="text-muted mb-4"></p>
                <div className="table-responsive">
                  {loading ? (
                    <ShimmerThumbnail height={650} rounded />
                  ) : Object.values(couponDetails).length === 0 ? (
                    <p className="text-center my-3">
                      {t("No wallet coupon details found")}
                    </p>
                  ) : (
                    <Table className="table-nowrap mb-0 capitalize_td">
                      <tbody>
                        <tr>
                          <th scope="row">{t("Id")}</th>
                          <td>{couponDetails._id}</td>
                        </tr>
                        <tr>
                          <th scope="row">{t("Name(English)")}</th>
                          <td>{couponDetails?.name}</td>
                        </tr>
                        <tr>
                          <th scope="row">{t("Name(عربي)")}</th>
                          <td>{couponDetails?.nameAr}</td>
                        </tr>
                        <tr>
                          <th scope="row">{t("Description(English)")}</th>
                          <td>{couponDetails?.description || "-"}</td>
                        </tr>
                        <tr>
                          <th scope="row">{t("Description(عربي)")}</th>
                          <td>{couponDetails?.descriptionAr || "-"}</td>
                        </tr>
                        <tr>
                          <th scope="row">{t("Coupon Code")}</th>
                          <td>{couponDetails?.code}</td>
                        </tr>
                        <tr>
                          <th scope="row">{t("Source Type")}</th>
                          <td>
                            {couponDetails?.sourceType === "assigned"
                              ? t("Assigned")
                              : t("Open")}
                          </td>
                        </tr>
                        <tr>
                          <th scope="row">{t("Usage Count")}</th>
                          <td>{couponDetails?.redemptions?.length ?? 0}</td>
                        </tr>
                        {couponDetails?.maxCount != null && (
                          <tr>
                            <th scope="row">{t("Max Redemptions")}</th>
                            <td>{couponDetails.maxCount}</td>
                          </tr>
                        )}
                        <tr>
                          <th scope="row">{t("Starts At")}</th>
                          <td>
                            {formatDateTime(couponDetails?.startsAt) || "-"}
                          </td>
                        </tr>

                        <tr>
                          <th scope="row">{t("Expires At")}</th>
                          <td>
                            {formatDateTime(couponDetails?.expiresAt) || "-"}
                          </td>
                        </tr>
                        <tr>
                          <th scope="row">{t("Status")}</th>
                          <td>
                            {couponDetails?.status === 1 ? (
                              <Badge className="bg-success">
                                {t("Active")}
                              </Badge>
                            ) : (
                              <Badge className="bg-danger">
                                {t("Deactive")}
                              </Badge>
                            )}
                          </td>
                        </tr>

                        <tr>
                          <th scope="row">{t("Created By")}</th>
                          <td>
                            {couponDetails?.createdBy
                              ? `${couponDetails.createdBy.firstName || ""} ${
                                  couponDetails.createdBy.lastName || ""
                                }`.trim()
                              : "-"}
                          </td>
                        </tr>

                        <tr>
                          <th scope="row">{t("Created")}</th>
                          <td>
                            {formatDateTime(couponDetails?.createdAt) || "-"}
                          </td>
                        </tr>

                        <tr>
                          <th scope="row">{t("Last Updated By")}</th>
                          <td>
                            {couponDetails?.lastUpdatedBy
                              ? `${
                                  couponDetails.lastUpdatedBy.firstName || ""
                                } ${
                                  couponDetails.lastUpdatedBy.lastName || ""
                                }`.trim()
                              : "-"}
                          </td>
                        </tr>

                        <tr>
                          <th scope="row">{t("Updated")}</th>
                          <td>
                            {formatDateTime(couponDetails?.updatedAt) || "-"}
                          </td>
                        </tr>
                      </tbody>
                    </Table>
                  )}
                </div>
              </CardBody>
            </Card>
          </Col>
          <Col lg={6}>
            <Card className="card_100_24">
              <CardBody>
                <CardTitle className="d-flex align-items-center">
                  {t("Wallet Coupon Info")}
                </CardTitle>
                <p className="text-muted mb-4"></p>
                <div className="table-responsive">
                  {loading ? (
                    <ShimmerThumbnail height={650} rounded />
                  ) : Object.values(couponDetails).length === 0 ? (
                    <p className="text-center my-3">
                      {t("No wallet coupon details found")}
                    </p>
                  ) : (
                    <Table className="table-nowrap mb-0 capitalize_td">
                      <tbody>
                        <tr>
                          <th scope="row">{t("Wallet Amount")}</th>
                          <td>{couponDetails?.walletAmount ?? "-"}</td>
                        </tr>
                        {couponDetails?.fileName && (
                          <tr>
                            <th scope="row">{t("File Name")}</th>
                            <td>{couponDetails.fileName}</td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  )}
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Assigned Customers (when sourceType is assigned) */}
        {couponDetails?.sourceType === "assigned" &&
          (couponDetails?.assignedCustomers?.length > 0 ||
            couponDetails?.sentToPhones?.length > 0) && (
            <Row>
              <Col lg={12}>
                <Card className="card_100_24">
                  <CardBody>
                    <CardTitle>{t("Assigned Customers")}</CardTitle>
                    <p className="text-muted mb-2">
                      {t("Customers who can redeem this coupon")}
                    </p>
                    {couponDetails?.assignedCustomers?.length > 0 ? (
                      <>
                        {hasDeletedUsers && (
                          <Nav tabs className="mb-3">
                            <NavItem>
                              <NavLink
                                className={classnames({
                                  active: assignedTab === "assigned",
                                })}
                                onClick={() => setAssignedTab("assigned")}
                                style={{ cursor: "pointer" }}
                              >
                                {t("Assigned Customers")} ({activeCustomers.length})
                              </NavLink>
                            </NavItem>
                            <NavItem>
                              <NavLink
                                className={classnames({
                                  active: assignedTab === "deleted",
                                })}
                                onClick={() => setAssignedTab("deleted")}
                                style={{ cursor: "pointer" }}
                              >
                                {t("Deleted Users")} ({deletedCustomers.length})
                              </NavLink>
                            </NavItem>
                          </Nav>
                        )}
                        <TabContent
                          activeTab={assignedTab}
                          className={hasDeletedUsers ? "" : "d-none"}
                        >
                          <TabPane tabId="assigned">
                            {activeCustomers.length > 0 ? (
                              <Table className="table mb-0" size="sm">
                                <thead>
                                  <tr>
                                    <th>{t("Name")}</th>
                                    <th>{t("Phone Number")}</th>
                                    <th>{t("Status")}</th>
                                    <th>{t("Redeemed At")}</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {activeCustomers.map((ac, idx) => {
                                    const acUserId =
                                      ac.userId?._id || ac.userId
                                    const redemption =
                                      couponDetails?.redemptions?.find(
                                        r =>
                                          (r.userId?._id || r.userId) ===
                                          acUserId
                                      )
                                    const isRedeemed = !!redemption
                                    const name =
                                      ac.userId &&
                                      typeof ac.userId === "object"
                                        ? `${ac.userId.firstName || ""} ${
                                            ac.userId.lastName || ""
                                          }`.trim()
                                        : ac.userId || "-"
                                    const phone = ac.userId?.phoneNumber
                                      ? ac.userId.phoneCode
                                        ? `${ac.userId.phoneCode} ${ac.userId.phoneNumber}`
                                        : ac.userId.phoneNumber
                                      : "-"
                                    return (
                                      <tr key={idx}>
                                        <td>{name || "-"}</td>
                                        <td>
                                          {ac.userId?.phoneNumber ? (
                                            <LtrText>{phone}</LtrText>
                                          ) : (
                                            "-"
                                          )}
                                        </td>
                                        <td>
                                          {isRedeemed ? (
                                            <Badge className="bg-success">
                                              {t("Redeemed")}
                                            </Badge>
                                          ) : (
                                            <Badge className="bg-secondary">
                                              {t("Not Redeemed")}
                                            </Badge>
                                          )}
                                        </td>
                                        <td>
                                          {isRedeemed &&
                                          redemption?.redeemedAt
                                            ? formatDateTime(
                                                redemption.redeemedAt
                                              )
                                            : "-"}
                                        </td>
                                      </tr>
                                    )
                                  })}
                                </tbody>
                              </Table>
                            ) : (
                              <p className="text-muted mb-0">
                                {t("No assigned customers found")}
                              </p>
                            )}
                          </TabPane>
                          <TabPane tabId="deleted">
                            <Table className="table mb-0" size="sm">
                              <thead>
                                <tr>
                                  <th>{t("Name")}</th>
                                  <th>{t("Phone Number")}</th>
                                  <th>{t("Status")}</th>
                                  <th>{t("Redeemed At")}</th>
                                </tr>
                              </thead>
                              <tbody>
                                {deletedCustomers.map((ac, idx) => {
                                  const acUserId = ac.userId?._id || ac.userId
                                  const redemption =
                                    couponDetails?.redemptions?.find(
                                      r =>
                                        (r.userId?._id || r.userId) ===
                                        acUserId
                                    )
                                  const isRedeemed = !!redemption
                                  const name =
                                    ac.userId &&
                                    typeof ac.userId === "object"
                                      ? `${ac.userId.firstName || ""} ${
                                          ac.userId.lastName || ""
                                        }`.trim()
                                      : ac.userId || "-"
                                  const phone = ac.userId?.phoneNumber
                                    ? ac.userId.phoneCode
                                      ? `${ac.userId.phoneCode} ${ac.userId.phoneNumber}`
                                      : ac.userId.phoneNumber
                                    : "-"
                                  return (
                                    <tr key={idx}>
                                      <td>{name || "-"}</td>
                                      <td>
                                        {ac.userId?.phoneNumber ? (
                                          <LtrText>{phone}</LtrText>
                                        ) : (
                                          "-"
                                        )}
                                      </td>
                                      <td>
                                        {isRedeemed ? (
                                          <Badge className="bg-success">
                                            {t("Redeemed")}
                                          </Badge>
                                        ) : (
                                          <Badge className="bg-secondary">
                                            {t("Not Redeemed")}
                                          </Badge>
                                        )}
                                      </td>
                                      <td>
                                        {isRedeemed && redemption?.redeemedAt
                                          ? formatDateTime(
                                              redemption.redeemedAt
                                            )
                                          : "-"}
                                      </td>
                                    </tr>
                                  )
                                })}
                              </tbody>
                            </Table>
                          </TabPane>
                        </TabContent>
                        {!hasDeletedUsers && (
                          <Table className="table mb-0" size="sm">
                            <thead>
                              <tr>
                                <th>{t("Name")}</th>
                                <th>{t("Phone Number")}</th>
                                <th>{t("Status")}</th>
                                <th>{t("Redeemed At")}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {activeCustomers.map((ac, idx) => {
                                const acUserId = ac.userId?._id || ac.userId
                                const redemption =
                                  couponDetails?.redemptions?.find(
                                    r =>
                                      (r.userId?._id || r.userId) === acUserId
                                  )
                                const isRedeemed = !!redemption
                                const name =
                                  ac.userId && typeof ac.userId === "object"
                                    ? `${ac.userId.firstName || ""} ${
                                        ac.userId.lastName || ""
                                      }`.trim()
                                    : ac.userId || "-"
                                const phone = ac.userId?.phoneNumber
                                  ? ac.userId.phoneCode
                                    ? `${ac.userId.phoneCode} ${ac.userId.phoneNumber}`
                                    : ac.userId.phoneNumber
                                  : "-"
                                return (
                                  <tr key={idx}>
                                    <td>{name || "-"}</td>
                                    <td>
                                      {ac.userId?.phoneNumber ? (
                                        <LtrText>{phone}</LtrText>
                                      ) : (
                                        "-"
                                      )}
                                    </td>
                                    <td>
                                      {isRedeemed ? (
                                        <Badge className="bg-success">
                                          {t("Redeemed")}
                                        </Badge>
                                      ) : (
                                        <Badge className="bg-secondary">
                                          {t("Not Redeemed")}
                                        </Badge>
                                      )}
                                    </td>
                                    <td>
                                      {isRedeemed && redemption?.redeemedAt
                                        ? formatDateTime(redemption.redeemedAt)
                                        : "-"}
                                    </td>
                                  </tr>
                                )
                              })}
                            </tbody>
                          </Table>
                        )}
                      </>
                    ) : (
                      <div className="d-flex flex-wrap gap-2">
                        {couponDetails.sentToPhones?.map((phone, idx) => (
                          <Badge key={idx} color="secondary" className="p-2">
                            <LtrText>{phone}</LtrText>
                          </Badge>
                        ))}
                      </div>
                    )}
                  </CardBody>
                </Card>
              </Col>
            </Row>
          )}
          
        {couponDetails?.sourceType === "open" &&
          couponDetails?.redemptions?.length > 0 && (
            <Row>
              <Col lg={12}>
                <Card className="card_100_24">
                  <CardBody>
                    <CardTitle>{t("Redemptions")}</CardTitle>
                    <p className="text-muted mb-2">
                      {t("Customers who have redeemed this coupon")}
                    </p>
                    <Table className="table mb-0" size="sm">
                      <thead>
                        <tr>
                          <th>{t("Name")}</th>
                          <th>{t("Phone Number")}</th>
                          <th>{t("Redeemed At")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {couponDetails.redemptions.map((redemption, idx) => (
                          <tr key={idx}>
                            <td>
                              {redemption.userId?.firstName &&
                              redemption.userId?.lastName
                                ? `${redemption.userId.firstName} ${redemption.userId.lastName}`
                                : redemption.userId?.firstName ||
                                  redemption.userId?.lastName ||
                                  "-"}
                            </td>
                            <td>
                              {(redemption.userId?.phoneCode &&
                                redemption.userId?.phoneNumber) ||
                              redemption.userId?.phoneNumber ? (
                                <LtrText>
                                  {redemption.userId?.phoneCode &&
                                  redemption.userId?.phoneNumber
                                    ? `${redemption.userId.phoneCode} ${redemption.userId.phoneNumber}`
                                    : redemption.userId?.phoneNumber || "-"}
                                </LtrText>
                              ) : (
                                "-"
                              )}
                            </td>
                            <td>
                              {redemption?.redeemedAt
                                ? formatDateTime(redemption.redeemedAt)
                                : "-"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          )}
      </div>
    </div>
  )
}

export default CouponRedeemDetails
